import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "../data";

const TABS = ["All", "Chai & Tea", "Coffee", "Iced & Cold", "Brand Extras"];

const PRODUCTS = [
  {
    name: "Double Wall Cups",
    tag: "Coffee",
    desc: "Premium thermal protection for filter coffee, cappuccinos & lattes. Matte, gloss or gold-foil finishes.",
    specs: ["250–600 ml", "5K MOQ", "15 days"],
    img: IMAGES.doubleWall,
    span: "md:col-span-4",
    height: "h-72 sm:h-80",
    accent: "bg-[#FF5A36]",
  },
  {
    name: "Cutting Chai Cups",
    tag: "Chai & Tea",
    desc: "India's hardest-working cup — 120–200 ml minis built for tapris, tea stalls & office canteens.",
    specs: ["120–200 ml", "5K MOQ"],
    img: IMAGES.cuttingChai,
    span: "md:col-span-2",
    height: "h-72 sm:h-80",
    accent: "bg-[#0D9488]",
  },
  {
    name: "Single Wall Cold Cups",
    tag: "Iced & Cold",
    desc: "High-contrast colour pop for cold coffee, iced chai & thick shakes.",
    specs: ["350–600 ml", "5K MOQ"],
    img: IMAGES.singleWall,
    span: "md:col-span-2",
    height: "h-64 sm:h-72",
    accent: "bg-[#FBBF24]",
  },
  {
    name: "Ripple Wall Chai Cups",
    tag: "Chai & Tea",
    desc: "Grip-friendly ripple texture keeps masala chai piping hot and hands cool — no sleeve needed.",
    specs: ["250–350 ml", "5K MOQ", "15 days"],
    img: IMAGES.rippleWall,
    span: "md:col-span-4",
    height: "h-64 sm:h-72",
    accent: "bg-[#2E1065]",
  },
  {
    name: "Branded Tissues",
    tag: "Brand Extras",
    desc: "Soft 2-ply table tissues printed with your brand — every wipe is a reminder. Perfect cup companion.",
    specs: ["2-ply", "Custom print", "5K MOQ"],
    img: IMAGES.tissue,
    span: "md:col-span-3",
    height: "h-64 sm:h-72",
    accent: "bg-[#FF5A36]",
  },
  {
    name: "Glass Water Bottle · 1000 ml",
    tag: "Brand Extras",
    desc: "Reusable 1-litre glass bottles with your branding — permanent desk-side presence at cafés & offices.",
    specs: ["1000 ml", "Reusable glass", "5K MOQ"],
    img: IMAGES.glassBottle,
    span: "md:col-span-3",
    height: "h-64 sm:h-72",
    accent: "bg-[#0D9488]",
  },
];

export const Products = ({ onQuote }) => {
  const [tab, setTab] = useState("All");
  const visible = PRODUCTS.filter((p) => tab === "All" || p.tag === tab);

  return (
    <section id="products" className="relative py-24 sm:py-32 bg-[#F3EFEA]" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#FF5A36] mb-4" data-testid="products-eyebrow">
              The Lineup · Made for India
            </p>
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#2E1065] leading-tight" data-testid="products-headline">
              Cups that carry<br />your <span className="italic text-[#0D9488]">story.</span>
            </h2>
          </motion.div>

          <div className="flex flex-wrap gap-2" data-testid="product-filter-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`text-sm font-bold rounded-full px-4 py-2 transition-all duration-300 ${
                  tab === t
                    ? "bg-[#2E1065] text-[#FBBF24] shadow-lg shadow-[#2E1065]/20"
                    : "bg-white text-[#2E1065]/70 hover:bg-[#2E1065]/5 border border-[#2E1065]/10"
                }`}
                data-testid={`product-tab-${t.toLowerCase().replace(/ & | /g, "-")}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="mt-12 grid md:grid-cols-6 gap-5" data-testid="product-bento-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.name}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className={`group relative ${p.span} bg-white rounded-[2rem] overflow-hidden border border-[#2E1065]/10 hover:shadow-2xl hover:shadow-[#2E1065]/15 hover:-translate-y-1.5 transition-all duration-500`}
                data-testid={`product-card-${p.name.toLowerCase().replace(/ · | & | /g, "-")}`}
              >
                <div className={`relative ${p.height} overflow-hidden`}>
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2E1065]/70 via-transparent to-transparent" />
                  <span className={`absolute top-4 left-4 ${p.accent} text-white text-[11px] font-black uppercase tracking-widest rounded-full px-3 py-1.5`}>
                    {p.tag}
                  </span>
                </div>
                <div className="p-6 sm:p-7">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display font-bold text-xl sm:text-2xl text-[#2E1065]">{p.name}</h3>
                      <p className="text-sm text-[#2E1065]/60 mt-2 leading-relaxed max-w-md">{p.desc}</p>
                    </div>
                    <button
                      onClick={() => onQuote(p.name)}
                      className="shrink-0 w-11 h-11 rounded-full bg-[#2E1065] text-[#FBBF24] flex items-center justify-center hover:bg-[#FF5A36] hover:text-white hover:rotate-45 transition-all duration-300"
                      aria-label={`Get quote for ${p.name}`}
                      data-testid={`product-quote-button-${p.name.toLowerCase().replace(/ · | & | /g, "-")}`}
                    >
                      <ArrowUpRight className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-5">
                    {p.specs.map((s) => (
                      <span key={s} className="text-xs font-bold text-[#0D9488] bg-[#0D9488]/10 rounded-full px-3 py-1.5">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 relative rounded-[2rem] overflow-hidden group"
          data-testid="itparks-showcase"
        >
          <img
            src={IMAGES.itPark}
            alt="IT professionals having coffee in paper cups outside a tech park"
            className="w-full h-80 sm:h-96 object-cover transition-transform duration-700 group-hover:scale-105"
            data-testid="itparks-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#170A38]/90 via-[#2E1065]/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="p-8 sm:p-14 max-w-xl">
              <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#FBBF24] mb-3" data-testid="itparks-eyebrow">
                Where We Market
              </p>
              <h3 className="font-display font-extrabold text-2xl sm:text-4xl text-[#FAF7F2] leading-tight" data-testid="itparks-headline">
                Your brand, on Karnataka's biggest campuses.
              </h3>
              <p className="mt-4 text-sm sm:text-base text-[#FAF7F2]/80 leading-relaxed" data-testid="itparks-copy">
                Our cups, branded tissues and 1-litre glass water bottles are
                marketed across major IT parks in Karnataka — reaching thousands
                of professionals at every coffee break, every single day.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
